/* eslint-disable @typescript-eslint/no-explicit-any */
import { takeEvery, all, put, select } from 'redux-saga/effects'
import { Map } from 'features/maps/types'
import { setLayerPopupSettings, setPopupSettings, showGlobalBuilderError } from './builderSlice'
import { getAggregationExp } from './spatialIndexSagas'
import { ActionTypes } from '@carto/kepler.gl/actions'
import { Layer } from '@carto/kepler.gl/layers'
import { _validateVecExprSyntax as validateVecExprSyntax } from '@carto/api-client'
import {
  PopupEvent,
  PopupStyle,
  PopupFormatSettings,
  KeplerTableField,
  MapDatasetEnriched
} from 'features/builder/types'
import { getKeplerFieldFromDatasetScheme, isStaticSpatialIndexDataset } from './builderSliceUtils'
import { PayloadAction } from '@reduxjs/toolkit'
import { updateDataset } from './datasetSagas'
import { getEnrichedDatasetById, getLayerById, getLayersByDatasetId } from './keplerGlSlice'
import { getDatasetColumnsFromMap, isAggregatedDataset } from './datasetUtils'
import { getDatasetById } from 'features/maps/state/mapsSlice'
import { getFieldsFromTemplate } from 'features/builder/utils/popupUtils'

export function convertKeplerInteractionConfig(keplerVisState) {
  const { layers, interactionConfig } = keplerVisState

  const tooltipConfig = interactionConfig?.tooltip
  if (!tooltipConfig || !tooltipConfig.enabled || !layers?.length) {
    return { layers: [] }
  }

  const configByLayer = layers.reduce((byLayer, layer: Layer) => {
    const datasetFieldConfig = tooltipConfig.fieldsToShow?.[layer.config.dataId as string]
    if (!datasetFieldConfig) {
      return byLayer
    }
    return {
      ...byLayer,
      [layer.id]: {
        enabled: true,
        [PopupEvent.Hover]: { fields: datasetFieldConfig, style: PopupStyle.Light }
      }
    }
  }, {})

  return {
    layers: configByLayer
  }
}

export function* loadPopupSettings(currentMap: Map) {
  try {
    // try to reload settings saved under the new format
    let popupSettings = currentMap.keplerMapConfig?.config?.popupSettings
    // if none are defined, try importing settings from the kepler format
    if (!popupSettings) {
      popupSettings = convertKeplerInteractionConfig(currentMap.keplerMapConfig?.config?.visState)
    }
    if (popupSettings) {
      yield put(setPopupSettings(popupSettings))
    }
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

export function* getPopupSettings() {
  try {
    const popupSettings = yield select((state) => state.builder.popupSettings)
    return popupSettings
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

function* removeUnusedPopupSettings() {
  try {
    const popupSettings = yield select((state) => state.builder.popupSettings)
    const layers = yield select((state) => state.keplerGl.map.visState.layers)

    const cleanedLayersSettings = Object.entries(popupSettings.layers).reduce(
      (remainingLayers, [layerId, layerSetting]) => {
        if (layers.some((l: Layer) => l.id === layerId)) {
          return { ...remainingLayers, [layerId]: layerSetting }
        }
        return remainingLayers
      },
      {}
    )

    yield put(setPopupSettings({ ...popupSettings, layers: cleanedLayersSettings }))
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

function validateFields(settings: PopupFormatSettings, datasetFields: KeplerTableField[]): PopupFormatSettings {
  if (!settings) {
    return settings
  }
  if (settings.templateMode && (!settings.fields || settings.fields?.length === 0)) {
    settings = {
      ...settings,
      fields: getFieldsFromTemplate(settings.template, datasetFields)
    }
  }
  return {
    ...settings,
    fields: (settings.fields || []).filter((popupField) => {
      if (popupField.isExpression) {
        const validationContext = datasetFields.reduce((acc, { id }) => {
          acc[id] = 1
          return acc
        }, {})

        return validateVecExprSyntax(popupField.name, validationContext).valid
      }
      return datasetFields.find((datasetField) => datasetField.name === popupField.name)
    })
  }
}

export function* updatePopupSettingsWithValidation(datasetId: string) {
  try {
    const layers = yield select(getLayersByDatasetId, datasetId)
    const dataset: MapDatasetEnriched | null = yield select((state) => getEnrichedDatasetById(state, datasetId))
    if (!dataset) return

    const datasetFields = [...(dataset?.fields || [])]

    if (isStaticSpatialIndexDataset(dataset)) {
      datasetFields.push(getKeplerFieldFromDatasetScheme(dataset) as unknown as KeplerTableField)
    }

    for (const layer of layers) {
      const currentLayerSettings = yield select((state) => state.builder.popupSettings?.layers[layer.id])
      if (!currentLayerSettings) continue

      const validatedSettings = {
        ...currentLayerSettings,
        [PopupEvent.Click]: validateFields(currentLayerSettings[PopupEvent.Click], datasetFields),
        [PopupEvent.Hover]: validateFields(currentLayerSettings[PopupEvent.Hover], datasetFields)
      }

      yield put(setLayerPopupSettings({ layerId: layer.id, settings: validatedSettings }))
    }
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

function* syncDatasetWithPopupSettings(action: PayloadAction<{ layerId: string }>) {
  try {
    const { layerId } = action.payload
    const datasetId = yield select((state) => getLayerById(state, layerId)?.config?.dataId)
    const dataset = yield select((state) => getDatasetById(state, datasetId))

    const columns = yield getDatasetColumnsFromMap(datasetId)
    const aggregationExp = isAggregatedDataset(dataset) ? yield getAggregationExp(datasetId) : null

    yield updateDataset(datasetId, { columns, aggregationExp })
  } catch (e: any) {
    const msg = `Error synching Popup Settings: ${e.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

function* watchMap() {
  yield takeEvery(ActionTypes.REMOVE_DATASET, removeUnusedPopupSettings)
  yield takeEvery(ActionTypes.REMOVE_LAYER, removeUnusedPopupSettings)
  yield takeEvery(setLayerPopupSettings.type, syncDatasetWithPopupSettings)
}

export default function* popupSagas() {
  try {
    yield all([watchMap()])
  } catch (e: any) {
    //TODO
  }
}
